import { OrderDirection } from "@/comments/__generated__/CommentsSectionContentPaginationQuery.graphql"
import ProductMembersListFloatingBar from "@/product/member/common/list/floating-bar/ProductMembersListFloatingBar"
import {
  mapOrderByFieldToColumn,
  mapSelectedGroupFilterToInput,
  mapSelectedProfileFieldsToInput,
} from "@/product/member/page/ProductMembersListPageToolbar"
import { ProductMembersListToolbarState } from "@/product/member/page/useProductMembersListToolbarState"
import ExperienceMembershipsTableRow, {
  ExperienceMembershipsTableRowSkeleton,
} from "@/product/settings/ExperienceMembershipsTableRow"
import { ExperienceMembershipsTablePaginationQuery } from "@/product/settings/__generated__/ExperienceMembershipsTablePaginationQuery.graphql"
import { ExperienceMembershipsTableQuery } from "@/product/settings/__generated__/ExperienceMembershipsTableQuery.graphql"
import { ExperienceMembershipsTable_PaginationFragment$key } from "@/product/settings/__generated__/ExperienceMembershipsTable_PaginationFragment.graphql"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import {
  DiscoCheckbox,
  DiscoCheckboxSkeleton,
  DiscoTable,
  DiscoTableSkeletonWithHeader,
} from "@disco-ui"
import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"
import {
  useLazyLoadQuery,
  usePaginationFragment,
  useSubscribeToInvalidationState,
} from "react-relay"
import { graphql } from "relay-runtime"

interface Props {
  productId: GlobalID
  toolbarState: ProductMembersListToolbarState
}

const MEMBERSHIPS_PER_PAGE = 5

function ExperienceMembershipsTable({ productId, toolbarState }: Props) {
  const [activePage, setActivePage] = useState(1)
  const [selectedMemberships, setSelectedMemberships] = useState<
    { id: GlobalID; memberId: GlobalID }[]
  >([])
  const [isSelectAllActive, setIsSelectAllActive] = useState(false)

  const args = {
    first: MEMBERSHIPS_PER_PAGE,
    id: productId,
    search: toolbarState.search,
    productRoles: toolbarState.selectedRole?.value
      ? [toolbarState.selectedRole.value]
      : null,
    memberGroupFilterInput: mapSelectedGroupFilterToInput(toolbarState),
    profileFields: mapSelectedProfileFieldsToInput(toolbarState),
    orderBy: {
      field: mapOrderByFieldToColumn(toolbarState.sort.order.field),
      direction: toolbarState.direction as OrderDirection,
    },
  }
  const { product } = useLazyLoadQuery<ExperienceMembershipsTableQuery>(
    graphql`
      query ExperienceMembershipsTableQuery(
        $id: ID!
        $first: Int!
        $after: String
        $last: Int
        $before: String
        $search: String
        $productRoles: [ProductRole!]
        $memberGroupFilterInput: MemberGroupFilterInput
        $profileFields: [OrganizationMembershipsProfileFieldFilter!]
        $orderBy: OrderByInput
      ) {
        product: node(id: $id) {
          ... on Product {
            ...ExperienceMembershipsTable_PaginationFragment
              @arguments(
                first: $first
                after: $after
                last: $last
                before: $before
                search: $search
                productRoles: $productRoles
                memberGroupFilterInput: $memberGroupFilterInput
                profileFields: $profileFields
                orderBy: $orderBy
              )
          }
        }
      }
    `,
    args,
    { fetchPolicy: "network-only" }
  )

  const { data, refetch } = usePaginationFragment<
    ExperienceMembershipsTablePaginationQuery,
    ExperienceMembershipsTable_PaginationFragment$key
  >(
    graphql`
      fragment ExperienceMembershipsTable_PaginationFragment on Product
      @refetchable(queryName: "ExperienceMembershipsTablePaginationQuery")
      @argumentDefinitions(
        first: { type: "Int!" }
        after: { type: "String" }
        last: { type: "Int" }
        before: { type: "String" }
        search: { type: "String" }
        orderBy: { type: "OrderByInput" }
        productRoles: { type: "[ProductRole!]" }
        memberGroupFilterInput: { type: "MemberGroupFilterInput" }
        profileFields: { type: "[OrganizationMembershipsProfileFieldFilter!]" }
      ) {
        productMemberships(
          first: $first
          after: $after
          last: $last
          before: $before
          search: $search
          orderBy: $orderBy
          roles: $productRoles
          memberGroupFilterInput: $memberGroupFilterInput
          profileFields: $profileFields
        ) @connection(key: "ExperienceMembershipsTable__productMemberships") {
          __id
          totalCount
          edges {
            cursor
            node {
              id
              memberId
              ...ExperienceMembershipsTableRowFragment
            }
          }
          pageInfo {
            endCursor
            startCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
    product
  )

  useSubscribeToInvalidationState(
    data?.productMemberships?.__id ? [data.productMemberships.__id] : [],
    refetchMemberships
  )

  // Reset selections when filters get applied
  useEffect(() => {
    handleCloseFloatingBar()
  }, [toolbarState])

  if (!data) return null

  const productMemberships = Relay.connectionToArray(data.productMemberships)

  return (
    <>
      <DiscoTable
        testid={"ExperienceMembershipsTable"}
        sectionProps={{ padding: 0 }}
        activePage={activePage}
        setActivePage={setActivePage}
        rowsPerPage={MEMBERSHIPS_PER_PAGE}
        onPaginate={refetch}
        filterDeps={[toolbarState]}
        connection={{
          cursorsList: data.productMemberships.edges.map((e) => e.cursor),
          totalCount: data.productMemberships.totalCount,
          pageInfo: {
            endCursor: data.productMemberships.pageInfo.endCursor,
            startCursor: data.productMemberships.pageInfo.startCursor,
          },
        }}
        header={[
          {
            value: (
              <DiscoCheckbox
                label={null}
                onClick={handleSelectAllMembersInProduct}
                checked={isSelectAllActive}
              />
            ),
          },
          { value: "Name" },
          { value: "Role" },
          { value: "Joined On" },
          { value: "Actions" },
        ]}
        rows={productMemberships.map((pm, i) => (
          <ExperienceMembershipsTableRow
            key={pm.id}
            testid={`ExperienceMembershipsTableRow.${i}`}
            productMembershipKey={pm}
            selected={isMembershipSelected(pm.id)}
            onToggle={handleSelectMember}
          />
        ))}
      />

      <ProductMembersListFloatingBar
        memberships={isSelectAllActive ? undefined : selectedMemberships}
        allSelected={
          isSelectAllActive
            ? {
                isAllSelected: isSelectAllActive,
                totalCount: data.productMemberships.totalCount,
              }
            : undefined
        }
        onClose={handleCloseFloatingBar}
        selectedGroupFilter={
          toolbarState.selectedGroup.value === "all"
            ? undefined
            : toolbarState.selectedGroup.value
        }
      />
    </>
  )

  function handleCloseFloatingBar() {
    setSelectedMemberships([])
    setIsSelectAllActive(false)
  }

  function refetchMemberships() {
    refetch(args, { fetchPolicy: "network-only" })
  }

  function handleSelectAllMembersInProduct() {
    setIsSelectAllActive((prev) => !prev)
  }

  function isMembershipSelected(productMembershipId: GlobalID) {
    if (isSelectAllActive) return true
    return selectedMemberships.map((sm) => sm.id).includes(productMembershipId)
  }

  function handleSelectMember(productMembership: { id: GlobalID; memberId: GlobalID }) {
    const isMemberSelected = isMembershipSelected(productMembership.id)
    let newSelectedMemberships: {
      id: string
      memberId: string
    }[]

    if (isMemberSelected) {
      // de-select
      if (isSelectAllActive) {
        setIsSelectAllActive(false)
        newSelectedMemberships = productMemberships
          .filter((m) => m.id !== productMembership.id)
          .map((pm) => {
            return { id: pm.id, memberId: pm.memberId }
          })
      } else {
        newSelectedMemberships = selectedMemberships.filter(
          (m) => m.id !== productMembership.id
        )
      }
    } else {
      // select
      newSelectedMemberships = [
        ...selectedMemberships,
        { id: productMembership.id, memberId: productMembership.memberId },
      ]
    }
    setSelectedMemberships(newSelectedMemberships)
  }
}

export const ExperienceMembershipsTableSkeleton = () => {
  return (
    <>
      <DiscoTableSkeletonWithHeader
        sectionProps={{ padding: 0 }}
        header={[
          { value: <DiscoCheckboxSkeleton hideLabel /> },
          { value: "Name" },
          { value: "Role" },
          { value: "Joined On" },
          { value: "Actions" },
        ]}
        rows={5}
        row={<ExperienceMembershipsTableRowSkeleton />}
      />
    </>
  )
}

export default Relay.withSkeleton<Props>({
  component: observer(ExperienceMembershipsTable),
  skeleton: ExperienceMembershipsTableSkeleton,
})
